/**
 * Copy text to clipboard with fallback for environments where Clipboard API is blocked
 * @param text - The text to copy
 * @returns Promise that resolves to true if successful, false otherwise
 */
export async function copyToClipboard(text) {
  // Check if Clipboard API is available and not restricted
  const canUseClipboardAPI = async () => {
    if (!navigator.clipboard || !navigator.clipboard.writeText) {
      return false;
    }

    // Check for permissions if available
    if (navigator.permissions) {
      try {
        const result = await navigator.permissions.query({
          name: "clipboard-write",
        });
        return result.state === "granted" || result.state === "prompt";
      } catch (err) {
        // Permissions API might not be available or clipboard-write not supported
        // Try clipboard API anyway
        return true;
      }
    }

    return true;
  };

  // Try modern Clipboard API first if available and allowed
  if (await canUseClipboardAPI()) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      // Silently fall through to fallback - don't log to avoid console spam
    }
  }

  // Fallback method using execCommand
  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Make the textarea invisible and off-screen
    textArea.style.position = "fixed";
    textArea.style.top = "-9999px";
    textArea.style.left = "-9999px";
    textArea.style.width = "1px";
    textArea.style.height = "1px";
    textArea.style.padding = "0";
    textArea.style.border = "none";
    textArea.style.outline = "none";
    textArea.style.boxShadow = "none";
    textArea.style.background = "transparent";
    textArea.style.opacity = "0";
    textArea.setAttribute("readonly", "");

    document.body.appendChild(textArea);

    // Select the text
    if (navigator.userAgent.match(/ipad|iphone/i)) {
      // iOS specific handling
      const range = document.createRange();
      range.selectNodeContents(textArea);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      textArea.setSelectionRange(0, 999999);
    } else {
      textArea.select();
    }

    try {
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      return successful;
    } catch (err) {
      document.body.removeChild(textArea);
      return false;
    }
  } catch (err) {
    return false;
  }
}
